import { ApolloClient } from '@apollo/client';
import {
  AddVariantToCartDeliveryMutation,
  AddVariantToCartDeliveryMutationVariables,
  AddVariantToCartDeliveryWithPreviewDiscountMutation,
  AddVariantToCartDeliveryWithPreviewDiscountMutationVariables,
  MutationAddVariantToCartDeliveryV2Args,
  RemoveVariantFromCartDeliveryMutation,
  RemoveVariantFromCartDeliveryMutationVariables,
  RemoveVariantFromCartDeliveryWithPreviewDiscountMutation,
  RemoveVariantFromCartDeliveryWithPreviewDiscountMutationVariables,
  MutationRemoveVariantFromCartDeliveryV2Args,
} from 'src/ts/@types/generated/graphql/cart-order-service/graphql';
import { FunnelCart } from 'src/ts/types/cart';
import {
  ADD_VARIANT_TO_CART_DELIVERY,
  ADD_VARIANT_TO_CART_DELIVERY_WITH_PREVIEW_DISCOUNT,
  REMOVE_VARIANT_FROM_CART_DELIVERY,
  REMOVE_VARIANT_FROM_CART_DELIVERY_WITH_PREVIEW_DISCOUNT,
} from 'src/graphql-operations/cart-order-service/carts.mutation';
import { ClientNames } from 'src/utils/apolloClient';

type AddVariantResult =
  | { success: true; cart: FunnelCart }
  | {
      success: false;
      error: 'STATUS_ERROR' | 'SOLD_OUT' | 'UNKNOWN';
    };

export async function addVariantToCartDelivery(input: {
  apolloClient: ApolloClient;
  args: MutationAddVariantToCartDeliveryV2Args;
  previewFunnelDiscountCode: boolean;
}): Promise<AddVariantResult> {
  const { apolloClient, args, previewFunnelDiscountCode } = input;

  let addVariantToCartDeliveryResponse:
    AddVariantToCartDeliveryMutation['addVariantToCartDelivery'] | undefined;

  if (previewFunnelDiscountCode) {
    const { data } = await apolloClient.mutate<
      AddVariantToCartDeliveryWithPreviewDiscountMutation,
      AddVariantToCartDeliveryWithPreviewDiscountMutationVariables
    >({
      mutation: ADD_VARIANT_TO_CART_DELIVERY_WITH_PREVIEW_DISCOUNT,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: { ...args, previewFunnelDiscountCode },
    });
    addVariantToCartDeliveryResponse = data?.addVariantToCartDelivery;
  } else {
    const { data } = await apolloClient.mutate<
      AddVariantToCartDeliveryMutation,
      AddVariantToCartDeliveryMutationVariables
    >({
      mutation: ADD_VARIANT_TO_CART_DELIVERY,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: { ...args },
    });
    addVariantToCartDeliveryResponse = data?.addVariantToCartDelivery;
  }

  // This is technically possible and we have no idea what went wrong.
  if (!addVariantToCartDeliveryResponse) {
    return { success: false, error: 'UNKNOWN' };
  }

  // Success case
  if (addVariantToCartDeliveryResponse.__typename === 'CartDelivery') {
    return { success: true, cart: addVariantToCartDeliveryResponse.cart };
  }

  // Error cases
  // For some reason the `validation` and `status` fields are nullable under the
  // CartError type, hence all of the type checking
  if (addVariantToCartDeliveryResponse.__typename === 'CartError') {
    if (addVariantToCartDeliveryResponse.validation?.status?.length) {
      return { success: false, error: 'STATUS_ERROR' };
    }

    if (addVariantToCartDeliveryResponse.validation?.soldOutItem?.length) {
      return {
        success: false,
        error: 'SOLD_OUT',
      };
    }
  }

  // Otherwise we have a status we don't know how to handle
  return { success: false, error: 'UNKNOWN' };
}

type RemoveVariantResult =
  | { success: true; cart: FunnelCart }
  | {
      success: false;
      error: 'STATUS_ERROR' | 'UNKNOWN';
    };

export async function removeVariantFromCartDelivery(input: {
  apolloClient: ApolloClient;
  args: MutationRemoveVariantFromCartDeliveryV2Args;
  previewFunnelDiscountCode: boolean;
}): Promise<RemoveVariantResult> {
  const { apolloClient, args, previewFunnelDiscountCode } = input;

  let removeVariantFromCartDeliveryResponse:
    | RemoveVariantFromCartDeliveryMutation['removeVariantFromCartDelivery']
    | undefined;

  if (previewFunnelDiscountCode) {
    const { data } = await apolloClient.mutate<
      RemoveVariantFromCartDeliveryWithPreviewDiscountMutation,
      RemoveVariantFromCartDeliveryWithPreviewDiscountMutationVariables
    >({
      mutation: REMOVE_VARIANT_FROM_CART_DELIVERY_WITH_PREVIEW_DISCOUNT,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: { ...args, previewFunnelDiscountCode },
    });
    removeVariantFromCartDeliveryResponse = data?.removeVariantFromCartDelivery;
  } else {
    const { data } = await apolloClient.mutate<
      RemoveVariantFromCartDeliveryMutation,
      RemoveVariantFromCartDeliveryMutationVariables
    >({
      mutation: REMOVE_VARIANT_FROM_CART_DELIVERY,
      context: { clientName: ClientNames.CART_ORDER_GRAPH },
      variables: { ...args },
    });
    removeVariantFromCartDeliveryResponse = data?.removeVariantFromCartDelivery;
  }

  // This is technically possible and we have no idea what went wrong.
  if (!removeVariantFromCartDeliveryResponse) {
    return { success: false, error: 'UNKNOWN' };
  }

  // Success case
  if (removeVariantFromCartDeliveryResponse.__typename === 'CartDelivery') {
    return { success: true, cart: removeVariantFromCartDeliveryResponse.cart };
  }

  // Error cases
  // For some reason the `validation` and `status` fields are nullable under the
  // CartError type, hence all of the type checking
  if (removeVariantFromCartDeliveryResponse.__typename === 'CartError') {
    if (removeVariantFromCartDeliveryResponse.validation?.status?.length) {
      return { success: false, error: 'STATUS_ERROR' };
    }
  }

  // Otherwise we have a status we don't know how to handle
  return { success: false, error: 'UNKNOWN' };
}
